import { FormInputField, FormInputFieldStatus } from '../../../contracts/form-input-field.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { SerializationContext, SerializedData, DataModelBuilder } from '../../dto/data-model.js';
import { isSameData } from '../../dto/data-model-utils.js';
import { LocalizedString } from '../localized-string.js';
import { VersionRange } from '../../core/version-range.js';
import { toSafeFilename } from '../../../utility/string.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

export class TextFormInputField extends FormInputField {
  static readonly TYPE_ID: string = 'ef01473c124e46b297010746bccbf02f';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly uniqueId: string,
    public readonly titles: LocalizedString,
    public readonly descriptions: LocalizedString,
    public readonly hints: LocalizedString,
    public readonly isRequired: boolean,

    // Set to [false] if it will contain short text (ex.: name, email, phone
    // number, etc.)
    // Set to [true] if it will contain long text (ex.: paragraph, answer to a
    // question, etc.)
    public readonly isMultiLine: boolean,
    public readonly status: FormInputFieldStatus,
  ) {
    super();
  }

  get $typeId(): string {
    return TextFormInputField.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, TextFormInputField.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): TextFormInputField {
    return new TextFormInputField(
        MapUtils.getString(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'descriptions')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'hints')),
        MapUtils.getBoolean(map, 'isRequired'),
        MapUtils.getBoolean(map, 'isMultiLine'),
        toEnumFromString(MapUtils.getString(map, '$status'), FormInputFieldStatus),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'titles': context.toData(this.titles),
      'descriptions': context.toData(this.descriptions),
      'hints': context.toData(this.hints),
      'isRequired': this.isRequired,
      'isMultiLine': this.isMultiLine,
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return new TextFormInputField(
        this.uniqueId,
        this.titles.clone(),
        this.descriptions.clone(),
        this.hints.clone(),
        this.isRequired,
        this.isMultiLine,
        this.status,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
       isSameData(other.titles, this.titles) &&
       isSameData(other.descriptions, this.descriptions) &&
       isSameData(other.hints, this.hints) &&
       other.isRequired === this.isRequired &&
       other.isMultiLine === this.isMultiLine &&
       other.status === this.status;
  }
}

export class TextFormInputFieldBuilder extends DataModelBuilder<TextFormInputField> {
  build(context: SerializationContext, map: SerializedData): TextFormInputField {
    return TextFormInputField.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TextFormInputField.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(TextFormInputField.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `isRequired` of type `boolean` and `uniqueId` of type `string` was introduced.
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'isRequired', true);
      MapUtils.setIfNullOrUndefined(map, 'uniqueId', toSafeFilename(
          context.fromData<LocalizedString>(MapUtils.getRecord(map, 'titles')).getAnyOrDefault({ defValue: 'default' }))
      );
      version = 2;
    }

    // In version 3 we added `$status` of type `FormInputFieldStatus`.
    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, '$status', FormInputFieldStatus.Live);
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
